import { defineAnalyticsEvent } from '@heidi/runtime/shell';

import type {
  ActiveSessionControlClickedEventProps,
  ActiveSessionIndicatorClickedEventProps,
  ActiveSessionNavigationEventProps,
} from '../contracts/active-session-analytics.types';
import type {
  PersonalizationUpdatedEventProps,
  SessionCreatedEventProps,
  SessionDeletedEventProps,
  SessionMarkedAsContextOnlyEventProps,
  SessionStartedWithDocumentQueueAnalyticsProps,
  SessionUpdatedEventProps,
  TranscriptDeletedEventProps,
} from '../contracts/session-lifecycle-analytics.types';
import type {
  SessionSearchUsageBaselineEventProps,
  SessionsFilterAppliedEventProps,
  SessionsListPaginatedEventProps,
  SessionsScopeChangedEventProps,
  SessionsSortChangedEventProps,
} from '../contracts/session-list-analytics.types';
import type {
  SessionMergeConfirmedEventProps,
  SessionMergeDissolveCancelledEventProps,
  SessionMergeDissolveConfirmedEventProps,
  SessionMergeDissolvePromptViewedEventProps,
  SessionMergeFailedEventProps,
  SessionMergeIgnoredEventProps,
  SessionMergeOnboardingAcceptedEventProps,
  SessionMergeOnboardingDismissedEventProps,
  SessionMergeOnboardingViewedEventProps,
  SessionMergeRelinkedEventProps,
  SessionMergeReviewDismissedEventProps,
  SessionMergeReviewViewedEventProps,
  SessionMergeTabViewedEventProps,
  SessionMergeUnlinkCancelledEventProps,
  SessionMergeUnlinkConfirmedEventProps,
  SessionMergeUnlinkPromptViewedEventProps,
  SessionMergeUnlinkedEventProps,
} from '../contracts/session-merge-analytics.types';
import type {
  ScheduleSyncedEventProps,
  ScheduleUploadCompletedEventProps,
  ScheduleUploadProcessingFailedEventProps,
  ScheduleUploadPromoClickedEventProps,
  ScheduleUploadPromoViewedEventProps,
  ScheduleUploadSessionsCreatedEventProps,
  ScheduleUploadStartedEventProps,
  ScheduleViewedEventProps,
} from '../contracts/session-schedule-analytics.types';
import type {
  SessionDetailResumeStartedEventProps,
  SessionTabSwitchedEventProps,
  SessionTabViewedEventProps,
} from '../contracts/session-tab-analytics.types';

/**
 * The forty-three event names session owns, declared once each (D9 ruling 27).
 *
 * FROZEN: each `name` is a warehouse key, so a rename is a new event. `builds`
 * omitted throughout = cloud-only (D6). The emit and its routing stay app-side.
 *
 * Which of session's names are NOT here, and the emitter measurement behind
 * each: openspec task 2.1c, asserted in
 * `src/composition/session-analytics-declaration.test.ts`.
 */

// The schedule funnel: view/sync, then the CSV upload's four stages and the
// two-surface promo that sells it.
export const scheduleViewed = defineAnalyticsEvent<ScheduleViewedEventProps>()({
  name: 'schedule_viewed',
  description: 'The upcoming-sessions schedule was opened',
});

export const scheduleSynced = defineAnalyticsEvent<ScheduleSyncedEventProps>()({
  name: 'schedule_synced',
  description: 'The schedule was re-synced from the connected EHR',
});

export const scheduleUploadStarted =
  defineAnalyticsEvent<ScheduleUploadStartedEventProps>()({
    name: 'schedule_upload_started',
    description: 'A schedule file was handed to the upload area',
  });

export const scheduleUploadCompleted =
  defineAnalyticsEvent<ScheduleUploadCompletedEventProps>()({
    name: 'schedule_upload_completed',
    description: 'A schedule upload finished processing on the backend',
  });

export const scheduleUploadProcessingFailed =
  defineAnalyticsEvent<ScheduleUploadProcessingFailedEventProps>()({
    name: 'schedule_upload_processing_failed',
    description: 'A schedule upload failed before any session was created',
  });

export const scheduleUploadSessionsCreated =
  defineAnalyticsEvent<ScheduleUploadSessionsCreatedEventProps>()({
    name: 'schedule_upload_sessions_created',
    description: 'Sessions were bulk-created from a processed schedule upload',
  });

export const scheduleUploadPromoViewed =
  defineAnalyticsEvent<ScheduleUploadPromoViewedEventProps>()({
    name: 'schedule_upload_promo_viewed',
    description: 'The schedule-upload discovery tooltip or modal was shown',
  });

export const scheduleUploadPromoClicked =
  defineAnalyticsEvent<ScheduleUploadPromoClickedEventProps>()({
    name: 'schedule_upload_promo_clicked',
    description: "The schedule-upload discovery promo's CTA was clicked",
  });

// Session lifecycle. `session_updated` is ONE name over five edits,
// discriminated by `update_action` — five names would be five funnels.
export const sessionCreated = defineAnalyticsEvent<SessionCreatedEventProps>()({
  name: 'session_created',
  description: 'A session was created, carrying the entry point it started from',
});

export const sessionUpdated = defineAnalyticsEvent<SessionUpdatedEventProps>()({
  name: 'session_updated',
  description: 'A session field changed, discriminated by `update_action`',
});

// The merge funnel's review-sheet decision: one impression, three outcomes.
export const sessionMergeReviewViewed =
  defineAnalyticsEvent<SessionMergeReviewViewedEventProps>()({
    name: 'session_merge_review_viewed',
    description: 'The merge review dialog auto-opened on session detail',
  });

export const sessionMergeConfirmed =
  defineAnalyticsEvent<SessionMergeConfirmedEventProps>()({
    name: 'session_merge_confirmed',
    description: 'Merge was tapped and the session group was created',
  });

export const sessionMergeIgnored = defineAnalyticsEvent<SessionMergeIgnoredEventProps>()({
  name: 'session_merge_ignored',
  description: 'Ignore was tapped and the suggestion was discarded for good',
});

export const sessionMergeReviewDismissed =
  defineAnalyticsEvent<SessionMergeReviewDismissedEventProps>()({
    name: 'session_merge_review_dismissed',
    description: 'The merge review dialog was dismissed and will resurface',
  });

// The only failure signal in the funnel, so outcomes are never double counted.
export const sessionMergeFailed = defineAnalyticsEvent<SessionMergeFailedEventProps>()({
  name: 'session_merge_failed',
  description: 'A merge mutation failed, carrying which action it was',
});

// The merge tab: its impression, and the two corrections made from it.
export const sessionMergeTabViewed =
  defineAnalyticsEvent<SessionMergeTabViewedEventProps>()({
    name: 'session_merge_tab_viewed',
    description: 'The Session Merge tab was actively switched to',
  });

export const sessionMergeUnlinked =
  defineAnalyticsEvent<SessionMergeUnlinkedEventProps>()({
    name: 'session_merge_unlinked',
    description: 'A member was removed from the session group',
  });

export const sessionMergeRelinked =
  defineAnalyticsEvent<SessionMergeRelinkedEventProps>()({
    name: 'session_merge_relinked',
    description: 'A member was re-added to the session group',
  });

// The dissolve prompt (the unlink would empty the group) and its two answers.
export const sessionMergeDissolvePromptViewed =
  defineAnalyticsEvent<SessionMergeDissolvePromptViewedEventProps>()({
    name: 'session_merge_end_prompt_viewed',
    description: 'The "End this Session Merge?" confirmation opened',
  });

export const sessionMergeDissolveConfirmed =
  defineAnalyticsEvent<SessionMergeDissolveConfirmedEventProps>()({
    name: 'session_merge_end_confirmed',
    description: 'End Session Merge was tapped on the dissolve confirmation',
  });

export const sessionMergeDissolveCancelled =
  defineAnalyticsEvent<SessionMergeDissolveCancelledEventProps>()({
    name: 'session_merge_end_cancelled',
    description: 'The dissolve confirmation was dismissed without dissolving',
  });

// The unlink prompt (the group survives) and its two answers. A separate trio
// from the dissolve one above: folding them would merge two decisions.
export const sessionMergeUnlinkPromptViewed =
  defineAnalyticsEvent<SessionMergeUnlinkPromptViewedEventProps>()({
    name: 'session_merge_unlink_prompt_viewed',
    description: 'The "Unmerge this session?" confirmation opened',
  });

export const sessionMergeUnlinkConfirmed =
  defineAnalyticsEvent<SessionMergeUnlinkConfirmedEventProps>()({
    name: 'session_merge_unlink_confirmed',
    description: 'Unmerge was tapped on the unlink confirmation',
  });

export const sessionMergeUnlinkCancelled =
  defineAnalyticsEvent<SessionMergeUnlinkCancelledEventProps>()({
    name: 'session_merge_unlink_cancelled',
    description: 'The unlink confirmation was dismissed without unlinking',
  });

// The onboarding promo's adoption funnel. All three payloads are empty by Iglu
// contract, so the join to the sessions table happens in the warehouse.
export const sessionMergeOnboardingViewed =
  defineAnalyticsEvent<SessionMergeOnboardingViewedEventProps>()({
    name: 'session_merge_onboarding_viewed',
    description: 'The "Enable Session Merge" onboarding promo was shown',
  });

export const sessionMergeOnboardingAccepted =
  defineAnalyticsEvent<SessionMergeOnboardingAcceptedEventProps>()({
    name: 'session_merge_onboarding_accepted',
    description: 'Try now was tapped on the Session Merge onboarding promo',
  });

export const sessionMergeOnboardingDismissed =
  defineAnalyticsEvent<SessionMergeOnboardingDismissedEventProps>()({
    name: 'session_merge_onboarding_dismissed',
    description: 'The Session Merge onboarding promo was dismissed',
  });

// The sessions-list toolbar: filter, sort, scope, pagination, search.
// Emitted from `features/sessions/**` and the `Navigation/sessions` toolbar
// filter hooks — a session root plus one of the three surfaces 2.1c names.
export const sessionsFilterApplied =
  defineAnalyticsEvent<SessionsFilterAppliedEventProps>()({
    name: 'sessions_filter_applied',
    description: 'A filter was applied to the sessions list or the schedule',
  });

export const sessionsSortChanged = defineAnalyticsEvent<SessionsSortChangedEventProps>()({
  name: 'sessions_sort_changed',
  description: "The sessions list's sort column or direction changed",
});

export const sessionsScopeChanged =
  defineAnalyticsEvent<SessionsScopeChangedEventProps>()({
    name: 'sessions_scope_changed',
    description: 'The sessions list switched between the own and team-member scopes',
  });

export const sessionsListPaginated =
  defineAnalyticsEvent<SessionsListPaginatedEventProps>()({
    name: 'sessions_list_paginated',
    description: 'Another page of the sessions list was loaded',
  });

export const sessionSearchUsageBaseline =
  defineAnalyticsEvent<SessionSearchUsageBaselineEventProps>()({
    name: 'session_search_usage_baseline',
    description: 'A sessions-list search ran, carrying query length and result count',
  });

// The live-transcription (Record Anywhere) indicator, plus the guard that
// reports navigating away mid-recording. Emitted from the app shell only —
// `Navigation/LiveTranscriptionIndicator`, `use-active-recording-guard`.
export const activeSessionIndicatorClicked =
  defineAnalyticsEvent<ActiveSessionIndicatorClickedEventProps>()({
    name: 'active_session_indicator_clicked',
    description: 'The live-transcription indicator was clicked',
  });

export const activeSessionControlClicked =
  defineAnalyticsEvent<ActiveSessionControlClickedEventProps>()({
    name: 'active_session_control_clicked',
    description: 'A pause, resume or stop control on the live indicator was used',
  });

export const activeSessionNavigation =
  defineAnalyticsEvent<ActiveSessionNavigationEventProps>()({
    name: 'active_session_navigation',
    description: 'A navigation was attempted while a session was still recording',
  });

// Session detail. The tab pair reaches `container/session-page`, one of the
// three surfaces 2.1c names; the resume event is emitted from
// `utils/analytics/track-session-resume-events.ts` behind the desktop hooks.
export const sessionTabViewed = defineAnalyticsEvent<SessionTabViewedEventProps>()({
  name: 'session_tab_viewed',
  description: 'A session tab stopped being active, carrying its time on screen',
});

export const sessionTabSwitched = defineAnalyticsEvent<SessionTabSwitchedEventProps>()({
  name: 'session_tab_switched',
  description: 'The active tab changed within a session',
});

export const sessionDetailResumeStarted =
  defineAnalyticsEvent<SessionDetailResumeStartedEventProps>()({
    name: 'session_detail_resume_started',
    description: 'A session-detail Resume actually started capturing audio again',
  });

// The two deletions. Both emit through a `lib/analytics/scribe/` host called
// from the delete modals and their mutations — app shell plus the session-page
// surface. `session_deleted` is one name over two arms, by `delete_action`.
export const sessionDeleted = defineAnalyticsEvent<SessionDeletedEventProps>()({
  name: 'session_deleted',
  description: 'Sessions were deleted, discriminated by `delete_action`',
});

export const transcriptDeleted = defineAnalyticsEvent<TranscriptDeletedEventProps>()({
  name: 'transcript_deleted',
  description: "A session's transcript was deleted, leaving the session",
});

// Note personalization and the context-only mark, both emitted through a
// `lib/analytics/scribe/` host reached from `container/session-page` and the
// note tab pane.
export const personalizationUpdated =
  defineAnalyticsEvent<PersonalizationUpdatedEventProps>()({
    name: 'personalization_updated',
    description: 'Note personalization was turned on, off, or had its voice edited',
  });

export const sessionMarkedAsContextOnly =
  defineAnalyticsEvent<SessionMarkedAsContextOnlyEventProps>()({
    name: 'session_marked_as_context_only',
    description: 'A session was marked as context only for a later session',
  });

// The queued-document start, emitted from `container/ScribeTabs/index.v2.tsx`
// through `lib/analytics/scribe/documents.ts`. Only the session half of its
// payload is contract here — the contracts note says why.
export const sessionStartedWithDocumentQueue =
  defineAnalyticsEvent<SessionStartedWithDocumentQueueAnalyticsProps>()({
    name: 'session_started_with_document_queue',
    description: 'A session opened with at least one document template queued',
  });
